/**
 * Property Editor Hook
 * Handles property add/update/remove and JSON preview logic
 */

import type { Property } from '../../../../../map-editor-shared/store/objectTypeStore'

interface FormData {
  name: string
  icon: string
  color: string
  properties: { key: string; type: Property['type']; required: boolean }[]
}

export function usePropertyEditor(
  formData: FormData,
  setFormData: React.Dispatch<React.SetStateAction<FormData>>
) {
  const addProperty = () => {
    setFormData(prev => ({
      ...prev,
      properties: [
        ...prev.properties,
        { key: '', type: 'string' as const, required: false },
      ],
    }))
  }

  const updateProperty = (index: number, updates: Partial<Property>) => {
    setFormData(prev => {
      const newProperties = [...prev.properties]
      newProperties[index] = { ...newProperties[index], ...updates }
      return { ...prev, properties: newProperties }
    })
  }

  const removeProperty = (index: number) => {
    setFormData(prev => ({
      ...prev,
      properties: prev.properties.filter((_, i) => i !== index),
    }))
  }

  const getZeroValue = (type: Property['type']) => {
    switch (type) {
      case 'string': return ""
      case 'number': return 0
      case 'boolean': return false
      case 'array': return []
      default: return null
    }
  }

  const getPreviewData = () => {
    const preview: Record<string, any> = {
      name: formData.name,
      icon: formData.icon ? '(Asset URL)' : '',
      color: formData.color,
    }

    formData.properties.forEach(prop => {
      if (prop.key) {
        preview[prop.key] = getZeroValue(prop.type)
      }
    })

    return preview
  }

  return {
    addProperty,
    updateProperty,
    removeProperty,
    getPreviewData,
  }
}
